/**
 * Usage Management Middleware
 * 
 * Provides wrapper functions and middleware for integrating usage checks
 * into existing operations. Follows the pattern of checking usage before
 * performing operations and handling insufficient usage gracefully.
 */

import { usageService } from '../services/usageService';
import { debugLog, debugError } from '@/lib/debugUtils';
import type {
  UsageType,
  UsageCheckParams,
  UsageCheckResult,
  UsageServiceError
} from '../types/usage';

// ============================================================================
// Core Middleware Function
// ============================================================================

/**
 * Higher-order function that wraps operations with usage checking
 * 
 * @param userId - User performing the operation
 * @param operationType - Type of operation (search, ai_analysis, etc.)
 * @param operation - The actual operation to perform
 * @param workspaceId - Optional workspace context
 * @param metadata - Optional operation metadata
 * @returns Promise resolving to operation result with usage information
 */
export async function withUsageCheck<T>(
  userId: string,
  operationType: UsageType,
  operation: () => Promise<T>,
  workspaceId?: string,
  metadata?: Record<string, any>
): Promise<{
  result: T;
  usageType: string;
  usageResult: UsageCheckResult;
}> {
  debugLog('UsageMiddleware: Starting operation with usage check', {
    userId,
    operationType,
    workspaceId,
    metadata
  });

  try {
    // Check and consume usage (free or credit)
    const usageParams: UsageCheckParams = {
      userId,
      usageType: operationType,
      workspaceId,
      metadata
    };

    const usageResult = await usageService.checkAndConsumeUsage(usageParams);

    if (!usageResult.success) {
      debugError('UsageMiddleware: Usage check failed', {
        userId,
        operationType,
        message: usageResult.message
      });
      
      throw new Error(usageResult.message);
    }

    debugLog('UsageMiddleware: Usage check successful', {
      userId,
      operationType,
      usageType: usageResult.usage_type
    });

    // Execute the operation
    try {
      const result = await operation();
      
      debugLog('UsageMiddleware: Operation completed successfully', {
        userId,
        operationType,
        usageType: usageResult.usage_type
      });

      return {
        result,
        usageType: usageResult.usage_type,
        usageResult
      };

    } catch (operationError) {
      debugError('UsageMiddleware: Operation failed after usage consumed', {
        userId,
        operationType,
        error: operationError
      });

      // Note: Usage was already consumed at this point
      // In production, you might want to implement refunding for failed operations
      throw operationError;
    }

  } catch (error) {
    debugError('UsageMiddleware: Error in usage check or operation', {
      userId,
      operationType,
      error
    });
    throw error;
  }
}

// ============================================================================
// Convenience Wrapper Functions
// ============================================================================

/**
 * Wrapper for search operations with usage tracking
 */
export async function withSearchUsageCheck<T>(
  userId: string,
  searchOperation: () => Promise<T>,
  workspaceId?: string,
  searchQuery?: string
): Promise<{
  result: T;
  usageType: string;
  usageResult: UsageCheckResult;
}> {
  return withUsageCheck(
    userId,
    'search',
    searchOperation,
    workspaceId,
    { searchQuery, operationType: 'keyword_search' }
  );
}

/**
 * Wrapper for AI analysis operations with usage tracking
 */
export async function withAIAnalysisUsageCheck<T>(
  userId: string,
  analysisOperation: () => Promise<T>,
  workspaceId?: string,
  analysisType?: string
): Promise<{
  result: T;
  usageType: string;
  usageResult: UsageCheckResult;
}> {
  return withUsageCheck(
    userId,
    'ai_analysis',
    analysisOperation,
    workspaceId,
    { analysisType, operationType: 'ai_analysis' }
  );
}

/**
 * Wrapper for content generation operations with usage tracking
 */
export async function withContentGenerationUsageCheck<T>(
  userId: string,
  contentOperation: () => Promise<T>,
  workspaceId?: string,
  contentType?: string
): Promise<{
  result: T;
  usageType: string;
  usageResult: UsageCheckResult;
}> {
  return withUsageCheck(
    userId,
    'content_generation',
    contentOperation,
    workspaceId,
    { contentType, operationType: 'content_generation' }
  );
}

// ============================================================================
// Pre-check Functions (without consuming usage)
// ============================================================================

/**
 * Check if user can perform operation without consuming usage
 * Useful for UI state management and showing warnings
 */
export async function canPerformOperation(
  userId: string,
  operationType: UsageType
): Promise<{
  canPerform: boolean;
  reason?: string;
  usageType?: string;
  remainingFree?: number;
  remainingCredits?: number;
}> {
  try {
    const result = await usageService.canPerformOperation(userId, operationType);
    
    if (result.canPerform) {
      // Get remaining usage for UI display
      const summary = await usageService.getUsageSummary(userId);
      
      let remainingFree = 0;
      let remainingCredits = 0;
      
      if (operationType === 'search') {
        remainingFree = summary.daily_searches_remaining;
        remainingCredits = summary.search_credits;
      } else if (operationType === 'ai_analysis') {
        remainingFree = summary.weekly_ai_analyses_remaining;
        remainingCredits = summary.ai_analysis_credits;
      }
      
      return {
        canPerform: true,
        usageType: result.usageType,
        remainingFree,
        remainingCredits
      };
    }
    
    return {
      canPerform: false,
      reason: result.reason
    };
    
  } catch (error) {
    debugError('Error checking operation capability', error);
    return {
      canPerform: false,
      reason: 'Unable to check usage limits'
    };
  }
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Enhanced error handler for usage-related errors
 */
export function handleUsageError(error: any): {
  isUsageError: boolean;
  userMessage: string;
  errorType?: string;
  shouldShowPurchase: boolean;
} {
  if (error instanceof UsageServiceError) {
    return {
      isUsageError: true,
      userMessage: error.message,
      errorType: error.type,
      shouldShowPurchase: error.type === 'INSUFFICIENT_CREDITS' || error.type === 'FREE_LIMIT_EXCEEDED'
    };
  }

  // Check if it's a usage limit error from the database function
  if (error?.message?.includes('Insufficient credits') || 
      error?.message?.includes('limit reached')) {
    return {
      isUsageError: true,
      userMessage: error.message,
      shouldShowPurchase: true
    };
  }

  return {
    isUsageError: false,
    userMessage: error?.message || 'An unexpected error occurred',
    shouldShowPurchase: false
  };
}

/**
 * Generate user-friendly error messages for usage errors
 */
export function getUsageErrorMessage(
  operationType: UsageType,
  errorType: string,
  isOwner: boolean = false
): string {
  if (isOwner) {
    return 'An unexpected error occurred with your unlimited access. Please try again.';
  }

  switch (errorType) {
    case 'INSUFFICIENT_CREDITS':
    case 'FREE_LIMIT_EXCEEDED':
      if (operationType === 'search') {
        return 'You\'ve reached your daily search limit. Purchase credits to continue searching.';
      } else if (operationType === 'ai_analysis') {
        return 'You\'ve reached your weekly AI analysis limit. Purchase credits to continue analyzing.';
      }
      return 'You\'ve reached your usage limit. Purchase credits to continue.';
      
    case 'INVALID_USAGE_TYPE':
      return 'Invalid operation type. Please refresh the page and try again.';
      
    case 'USER_NOT_FOUND':
      return 'User account not found. Please sign in again.';
      
    case 'UNAUTHORIZED_ACCESS':
      return 'You don\'t have permission to perform this operation.';
      
    case 'DATABASE_ERROR':
      return 'A temporary error occurred. Please try again in a moment.';
      
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

// ============================================================================
// Usage Notification Helpers
// ============================================================================

/**
 * Check if user should see low usage warnings
 */
export async function shouldShowLowUsageWarning(
  userId: string,
  operationType: UsageType
): Promise<{
  shouldShow: boolean;
  warningType: 'low_free' | 'no_free' | 'low_credits' | 'no_credits' | null;
  message?: string;
}> {
  try {
    const summary = await usageService.getUsageSummary(userId);
    
    // Owners don't need warnings
    if (summary.is_owner) {
      return { shouldShow: false, warningType: null };
    }

    if (operationType === 'search') {
      const freeRemaining = summary.daily_searches_remaining;
      const credits = summary.search_credits;
      
      if (freeRemaining === 0 && credits === 0) {
        return {
          shouldShow: true,
          warningType: 'no_credits',
          message: 'You\'ve reached your daily search limit and have no search credits remaining.'
        };
      } else if (freeRemaining === 0 && credits <= 5) {
        return {
          shouldShow: true,
          warningType: 'low_credits',
          message: `You\'ve reached your daily search limit. You have ${credits} search credits remaining.`
        };
      } else if (freeRemaining <= 1) {
        return {
          shouldShow: true,
          warningType: 'low_free',
          message: `You have ${freeRemaining} free search${freeRemaining === 1 ? '' : 'es'} remaining today.`
        };
      }
    } else if (operationType === 'ai_analysis') {
      const freeRemaining = summary.weekly_ai_analyses_remaining;
      const credits = summary.ai_analysis_credits;
      
      if (freeRemaining === 0 && credits === 0) {
        return {
          shouldShow: true,
          warningType: 'no_credits',
          message: 'You\'ve reached your weekly AI analysis limit and have no AI analysis credits remaining.'
        };
      } else if (freeRemaining === 0 && credits <= 2) {
        return {
          shouldShow: true,
          warningType: 'low_credits',
          message: `You\'ve reached your weekly AI analysis limit. You have ${credits} AI analysis credits remaining.`
        };
      } else if (freeRemaining === 0) {
        return {
          shouldShow: true,
          warningType: 'no_free',
          message: 'You\'ve reached your weekly free AI analysis limit. Additional analyses will use your credits.'
        };
      }
    }
    
    return { shouldShow: false, warningType: null };
    
  } catch (error) {
    debugError('Error checking low usage warning', error);
    return { shouldShow: false, warningType: null };
  }
}

// ============================================================================
// Export all utilities
// ============================================================================

export const UsageMiddleware = {
  withUsageCheck,
  withSearchUsageCheck,
  withAIAnalysisUsageCheck,
  withContentGenerationUsageCheck,
  canPerformOperation,
  handleUsageError,
  getUsageErrorMessage,
  shouldShowLowUsageWarning
};